import { localStorage as browserLocalStorage } from '@play-co/browser-storage';
import type { ABTestsDynamicConfig } from '../../../common/ReplicantABTests';
import logger from '../../../logger';
import type { Message, Messages } from '../../../ReplicantMessages';
import { isEventHandlerMessage } from '../../../utils/MessageUtils';
import DB, {
    Changeset,
    ChatbotMetainfo,
    ConditionalWriteError,
    Entry,
    MetainfoMVCC,
    ScheduledAction,
    ScheduledActionKey,
} from '../../DB';
import { createChatbotMetainfo } from '../createChatbotMetainfo';
import { createDevDBMetainfoMVCC } from '../DevDBMetainfoMVCC';
import { createDevDBSharedStates } from '../DevDBSharedStates';
import { createDevDBTasks } from '../DevDBTasks';
import { deleteUserScheduledActions } from '../utils';

export const KEY_PREFIX = 'Replicantdb';

const scheduledActionKeyPrefix = (prefix: string, id: string) => prefix + '_scheduledactions_' + id;

function createLocalStorageDB<TState, TMessages extends Messages = any>(opts: {
    storage?: Storage;
    writeChangesets?: boolean;
    prefix?: string;
}): DB<TState, TMessages> {
    const localStorage = opts.storage ?? browserLocalStorage;

    const prefix = KEY_PREFIX + (opts.prefix ? '_' + opts.prefix : '');

    const stateKey = (id: string) => prefix + '_state_' + id;
    const metainfoMVCCKey = (id: string) => prefix + '_' + id + '_metainfomvcc';
    const chatbotMetainfoKey = (id: string) => prefix + '_' + id + '_chatbot';

    const msgKey = (id: string, msgId: string) => prefix + '_' + id + '_messages_' + msgId;
    const changesetKey = (id: string, revId: number) => prefix + '_' + id + '_changesets_' + revId;
    const sharedStateKey = ({ stateId, stateName }: { stateId: string; stateName: string }) =>
        `${prefix}_sharedStates_${stateName}_${stateId}`;
    const snapshotKey = (id: string, revId: number) => prefix + '_' + id + '_snapshots_' + revId;

    const scheduledActionKey = (id: string) => scheduledActionKeyPrefix(prefix, id);

    const lineChatbotBroadcastKey = prefix + '_lineChatbotBroadcasts';
    const loginTokenKey = (opts: { userId: string; token: string }) => `_loginToken_${opts.userId}_${opts.token}`;
    const kvKey = (id: string) => prefix + '_kv_' + id;
    const internalKvKey = (id: string) => prefix + '_ikv_' + id;
    const taskKey = (id: string) => prefix + '_task_' + id;
    const ecpmKey = prefix + '_ecpm';
    const exchangeRatesKey = prefix + '_exchange_rates';
    const abTestsKey = prefix + '_ab_tests';
    const userListsKey = prefix + '_ab_tests_user_lists';

    async function fetchMessages(id: string): Promise<Message<TMessages>[]> {
        validateUsernameForLocalstorage(id);

        // super inefficient, but whatever.
        const keys = getAllKeysFromLocalStorage(localStorage);

        const messages = keys
            .filter((x) => x.startsWith(prefix + '_' + id + '_messages_'))
            // In order to provide the same behavior as on DynamoDB, we need to sort the keys
            .sort()
            .map((x) => JSON.parse(localStorage.getItem(x)!));

        return messages;
    }

    const dbFetch: DB<TState, TMessages>['fetch'] = async (id, opts) => {
        const messages = await fetchMessages(id);

        const result = localStorage.getItem(stateKey(id));
        if (result) {
            const entry: Entry<TState> = JSON.parse(result);
            await migrateIfNeeded(id, entry);

            return {
                consumedCapacity: 0,
                entry: {
                    ...entry,
                    lastMessageReceivedAt:
                        messages.length > 0 ? Math.max(...messages.map((msg) => msg.timestamp)) : undefined,
                    mainItemRev: entry.rev,
                    metainfoMVCC: dbReadMetainfoMVCC(id),
                    chatbotMetainfo: await dbReadChatbotMetainfo(id),
                    userSharedStates: await dbSharedStates.fetchUserStates(id),
                },
                entryDiffs: [],
                messages,
            };
        }

        return { consumedCapacity: 0, entry: null, entryDiffs: [], messages };
    };

    const dbReadMetainfoMVCC = (id: string): MetainfoMVCC => {
        const res = localStorage.getItem(metainfoMVCCKey(id));
        return res ? JSON.parse(res) : {};
    };

    const dbUpdateMetainfoMVCC = (id: string, cb: (metainfo: MetainfoMVCC) => void): MetainfoMVCC => {
        const metainfo = dbReadMetainfoMVCC(id);
        cb(metainfo);
        localStorage.setItem(metainfoMVCCKey(id), JSON.stringify(metainfo));
        return metainfo;
    };

    const dbReadChatbotMetainfo = async (id: string): Promise<ChatbotMetainfo | undefined> => {
        const res = localStorage.getItem(chatbotMetainfoKey(id));
        return res ? JSON.parse(res) : null;
    };

    const dbWriteChatbotMetainfo = async (
        id: string,
        action: (metainfo?: ChatbotMetainfo) => Promise<ChatbotMetainfo>,
    ) => {
        const oldMetainfo = await dbReadChatbotMetainfo(id);
        const newMetainfo = await action(oldMetainfo);

        localStorage.setItem(chatbotMetainfoKey(id), JSON.stringify(newMetainfo));
        return newMetainfo;
    };

    function getItem<T = unknown>(id: string): T | null {
        return JSON.parse(localStorage.getItem(id) || 'null');
    }

    function setItem(id: string, value: unknown): void {
        localStorage.setItem(id, JSON.stringify(value));
    }

    const dbScheduledActions: DB<any>['scheduledActions'] = {
        writeBatch: async (newScheduledActions) => {
            for (const { action, key } of newScheduledActions) {
                const id = key.timeslotId + '_' + key.actionId;
                localStorage.setItem(scheduledActionKey(id), JSON.stringify(action));
            }
        },

        delete: async (key: ScheduledActionKey) => {
            const id = key.timeslotId + '_' + key.actionId;
            localStorage.removeItem(scheduledActionKey(id));
        },

        deleteBatch: async (keys: ScheduledActionKey[]) => {
            for (const key of keys) {
                const id = key.timeslotId + '_' + key.actionId;
                localStorage.removeItem(scheduledActionKey(id));
            }
        },

        fetch: async (key: ScheduledActionKey): Promise<ScheduledAction | null> => {
            const id = key.timeslotId + '_' + key.actionId;
            const res = localStorage.getItem(scheduledActionKey(id));
            if (res) {
                return JSON.parse(res);
            } else {
                return null;
            }
        },

        fetchAllForTimeslot: async (timeslotId) => {
            const timeslotKeys = getAllKeysFromLocalStorage(localStorage).filter((x) =>
                x.startsWith(scheduledActionKey(timeslotId + '_')),
            );

            const actions = timeslotKeys
                .map((key) => {
                    const eventId = key.substr(scheduledActionKey(timeslotId + '_').length);
                    return {
                        [eventId]: JSON.parse(localStorage.getItem(key)!),
                    };
                })
                .reduce((sum, x) => ({ ...sum, ...x }), {});

            return { actions };
        },
    };

    const dbSharedStates = createDevDBSharedStates({
        deleteSharedState: ({ stateId, stateName }) => localStorage.removeItem(sharedStateKey({ stateId, stateName })),

        getAllSharedStateIds: () =>
            getAllKeysFromLocalStorage(localStorage)
                .filter((key) => key.startsWith(`${prefix}_sharedStates_`))
                .map((key) => {
                    const parts = key.split('_');
                    const stateId = parts.at(-1);
                    const stateName = parts.at(-2);

                    if (!stateId || !stateName) {
                        throw Error(`Invalid shared state key ${key}`);
                    }

                    return { stateId, stateName };
                }),

        getSharedState: ({ stateId, stateName }) => getItem(sharedStateKey({ stateId, stateName })),

        setSharedState: ({ sharedState, stateId, stateName }) =>
            setItem(sharedStateKey({ stateId, stateName }), sharedState),
    });

    const dbWrite = async (id: string, { userSharedStates, ...payload }: Omit<Entry<TState>, 'mainItemRev'>) => {
        validateUsernameForLocalstorage(id);
        localStorage.setItem(stateKey(id), JSON.stringify(payload));
    };

    const fetchChangeset: DB<any>['fetchChangeset'] = async (id, rev) => {
        validateUsernameForLocalstorage(id);

        const result = localStorage.getItem(changesetKey(id, rev));

        if (!result) {
            return null;
        }

        const changeset = JSON.parse(result);
        changeset.date = new Date(changeset.date);
        return changeset;
    };

    const db: DB<TState, TMessages> = {
        fetch: dbFetch,
        write: async (id, payload, options) => {
            if (options?.noOverwrite && localStorage.getItem(stateKey(id))) {
                throw new Error('Entry already exists for ' + id);
            }

            return dbWrite(id, payload);
        },

        writeToDBConditionally: async (id, payload, rev) => {
            validateUsernameForLocalstorage(id);

            const { entry } = await dbFetch(id);
            if (!entry) {
                throw new Error('Could not load ' + id);
            }

            if (entry.rev === rev) {
                await dbWrite(id, payload);
                return JSON.stringify(payload).length;
            } else {
                throw new ConditionalWriteError(payload.rev);
            }
        },

        async writeEntryOrEntryDiff(opts) {
            const itemByteSize = await this.writeToDBConditionally(
                opts.userId,
                opts.snapshot.entry,
                opts.snapshot.entry.rev - 1,
            );
            return { itemByteSize };
        },

        writeMessages: async (id, msgs) => {
            validateUsernameForLocalstorage(id);

            for (const msg of msgs) {
                if (localStorage.getItem(msgKey(id, msg.id))) {
                    throw new Error('Message ' + msg.id + ' already exists!');
                }

                localStorage.setItem(msgKey(id, msg.id), JSON.stringify(msg));
            }
        },

        writeEventHandlerMessage: async (userId, msg) => db.writeMessages(userId, [msg] as any),

        deleteMessages: async (id, messages) => {
            validateUsernameForLocalstorage(id);

            messages.forEach((msg, i) => {
                const isDuplicate = messages.findIndex(({ id }) => id === msg.id) < i;

                if (isDuplicate) {
                    throw Error(`Duplicate message ID ${msg.id}`);
                }
            });

            for (const msg of messages) {
                localStorage.removeItem(msgKey(id, msg.id));
            }
        },

        fetchMessages,

        writeChangeset: async ({ id, diff, entry, date, appVersion, replication, messages, snapshotUpdate }) => {
            if (opts.writeChangesets) {
                validateUsernameForLocalstorage(id);

                const changesetMessages = messages.map((msg) => {
                    if (isEventHandlerMessage(msg)) {
                        const { metainfoDiff, stateDiff, ...msgWithoutDiff } = msg;
                        return msgWithoutDiff;
                    }

                    return msg;
                });

                const changeset: Changeset = {
                    rev: entry.rev,
                    version: entry.version,
                    diff,
                    appVersion,
                    date,
                    replication,
                    messages: changesetMessages,
                };
                localStorage.setItem(changesetKey(id, entry.rev), JSON.stringify(changeset));

                if (snapshotUpdate?.shouldWriteNewSnapshot) {
                    validateUsernameForLocalstorage(id);

                    const snapshot = {
                        ...entry,
                        lastInvokeTime: 0,
                        dailySnapshotAt: 0,
                        dailySnapshotRev: 0,
                        weeklySnapshotAt: 0,
                        weeklySnapshotRev: 0,
                        monthlySnapshotAt: 0,
                        monthlySnapshotRev: 0,
                    };
                    localStorage.setItem(snapshotKey(id, entry.rev), JSON.stringify(snapshot));
                }
            }
        },

        fetchChangeset,

        fetchChangesetsBetween: async (id, fromRev, toRev) => {
            const changesets = [];

            for (let rev = fromRev; rev <= toRev; rev++) {
                const changeset = await fetchChangeset(id, rev);
                if (changeset) {
                    changesets.push(changeset);
                }
            }

            return changesets;
        },

        fetchChangesetsPage: async (id) => {
            return { items: [], totalItemsCount: 0 };
        },

        fetchChangesetsPageByDate: async (id) => {
            return { items: [], totalItemsCount: 0, currentPage: 1 };
        },

        delete: async (id) => {
            validateUsernameForLocalstorage(id);

            await deleteUserScheduledActions(db, id);

            const keys = getAllKeysFromLocalStorage(localStorage);
            keys.filter(
                (x) =>
                    x.startsWith(prefix + '_state_' + id) ||
                    x.startsWith(prefix + '_' + id + '_messages_') ||
                    x.startsWith(prefix + '_' + id + '_changesets_') ||
                    x.startsWith(prefix + '_' + id + '_snapshots_') ||
                    x.startsWith(prefix + '_' + id + '_chatbot') ||
                    x.startsWith(prefix + '_' + id + '_metainfomvcc') ||
                    x.startsWith(prefix + '_' + id + '_chatbot'),
            ).forEach((x) => localStorage.removeItem(x));
        },

        nukeDB: async () => {
            const keys = getAllKeysFromLocalStorage(localStorage);
            keys.filter((x) => x.startsWith(prefix)).forEach((x) => localStorage.removeItem(x));
        },

        // Account linking is disabled in offline mode
        accountLink: {
            getPhoneNumber: async () => null,
            getUserId: async () => null,
            linkUserId: async () => undefined,
            setNotificationConsentText: async () => undefined,
        },

        // Inactive user archival is disabled in offline mode
        archive: {
            archiveUser: async () => undefined,
            restoreUserFromArchive: async () => null,
        },

        lineChatbotBroadcasts: {
            addBroadcast: async (broadcast) => {
                const broadcasts = await db.lineChatbotBroadcasts.getBroadcasts();
                const newBroadcasts = JSON.stringify([...broadcasts, broadcast]);

                localStorage.setItem(lineChatbotBroadcastKey, newBroadcasts);
            },
            getBroadcasts: async () => JSON.parse(localStorage.getItem(lineChatbotBroadcastKey) || '[]'),
        },

        loginTokens: {
            writeLoginToken: async (loginToken) =>
                localStorage.setItem(loginTokenKey(loginToken), JSON.stringify(loginToken)),
            fetchLoginToken: async (opts) => JSON.parse(localStorage.getItem(loginTokenKey(opts))!),
            deleteLoginToken: async (opts) => localStorage.removeItem(loginTokenKey(opts)),
        },

        // Entry diffs are disabled in offline mode
        entryDiff: {
            write: async () => ({ itemByteSize: 0 }),
            deleteBatch: async () => undefined,
        },

        kvStore: {
            write: async (key, value, timestamp) => {
                localStorage.setItem(kvKey(key), JSON.stringify({ key, value, timestamp }));
            },

            fetch: async (key) => {
                const result = localStorage.getItem(kvKey(key));
                return result ? JSON.parse(result).value : null;
            },

            delete: async (key) => {
                localStorage.removeItem(kvKey(key));
            },
        },

        internalKvStore: {
            write: async (key, value) => {
                localStorage.setItem(internalKvKey(key), value);
            },

            writeBatch: async (pairs) => {
                for (const key in pairs) {
                    localStorage.setItem(internalKvKey(key), pairs[key]!);
                }
            },

            fetch: async (key) => {
                return localStorage.getItem(internalKvKey(key));
            },

            delete: async (key) => {
                localStorage.removeItem(internalKvKey(key));
            },
        },

        lock: {
            acquireTimestampLock: async () => true, // DB locks are no-op in offline mode
        },

        metainfoMVCC: createDevDBMetainfoMVCC(dbReadMetainfoMVCC, dbUpdateMetainfoMVCC),

        // OTP is disabled in offline mode
        otp: {
            createVerification: async () => undefined,
            getVerification: async () => null,
        },

        chatbot: createChatbotMetainfo({
            fetchMetainfos: async (ids) => {
                const results: { [key: string]: ChatbotMetainfo } = {};
                for (const id of ids) {
                    const metainfo = await dbReadChatbotMetainfo(id);
                    if (metainfo) {
                        results[id] = metainfo;
                    }
                }
                return results;
            },
            writeChatbotMetainfo: dbWriteChatbotMetainfo,
        }),
        scheduledActions: dbScheduledActions,
        sharedStates: dbSharedStates,

        snapshots: {
            fetchSnapshot: async (id: string, rev: number) => {
                validateUsernameForLocalstorage(id);

                const result = localStorage.getItem(snapshotKey(id, rev));
                return result ? JSON.parse(result) : null;
            },
            fetchSnapshots: async (id: string) => {
                validateUsernameForLocalstorage(id);

                return getAllKeysFromLocalStorage(localStorage)
                    .filter((key) => key.startsWith(prefix + '_' + id + '_snapshots_'))
                    .map((key) => JSON.parse(localStorage.getItem(key)!));
            },
        },

        tasks: createDevDBTasks(
            (id) => JSON.parse(localStorage.getItem(taskKey(id)) || 'null'),
            (id, task) => localStorage.setItem(taskKey(id), JSON.stringify(task)),
        ),
        adminTasks: {} as any, // Admin tasks are not available on local environment

        // Social graph is not available on local environment:
        socialGraph: {
            addPlayerFriend: () => Promise.resolve(),
            createInteractions: () => Promise.resolve(),
            createOrUpdatePlayer: () => Promise.resolve(),
            getNonPlayers: () => Promise.resolve([]),
            getPlayer: () => Promise.resolve(null),
            getPlayers: () => Promise.resolve({}),
            removePlayerFriend: () => Promise.resolve(),
        },

        abTests: {
            userLists: {
                get: async () => JSON.parse(localStorage.getItem(userListsKey) || '[]'),
                set: async (payload) => localStorage.setItem(userListsKey, JSON.stringify(payload)),
            },

            get: async () => {
                const abTests = JSON.parse(localStorage.getItem(abTestsKey) || '{}') as ABTestsDynamicConfig;

                // Backwards compatibility.
                // Roll-out should default to 0, but if a test without roll-out is already active,
                // it should get 100% roll-out.
                for (const abTestId in abTests) {
                    const test = abTests[abTestId]!;
                    if (test.rollOut === undefined) {
                        test.rollOut = test.active ? 1 : 0;
                    }
                }

                return abTests;
            },
            set: async (payload) => localStorage.setItem(abTestsKey, JSON.stringify(payload)),
        },

        ecpm: {
            get: async () => {
                const data = localStorage.getItem(ecpmKey);
                return data ? JSON.parse(data) : null;
            },
            set: async (item) => {
                localStorage.setItem(ecpmKey, JSON.stringify(item));
            },
        },

        exchangeRates: {
            get: async () => {
                const data = localStorage.getItem(exchangeRatesKey);
                return data ? JSON.parse(data) : { rates: {}, timestamp: 0 };
            },

            set: async (payload) => {
                localStorage.setItem(exchangeRatesKey, JSON.stringify(payload));
            },

            setTimestamp: async (timestamp) => {
                const payload = await db.exchangeRates.get();

                payload.timestamp = timestamp;

                await db.exchangeRates.set(payload);
            },
        },
    };

    async function migrateIfNeeded(id: string, entry: Entry<TState>) {
        // CreatedAt field is not present on all entries, but we reconstruct it.
        if (!entry.createdAt) {
            // Populate from changeset, if available.
            const changeset = await db.fetchChangeset(id, 1);
            entry.createdAt = changeset ? changeset.date.getTime() : entry.lastUpdated;
            await dbWrite(id, entry);
        }
    }

    // Expose nuking db to 'window' for easy DB nuking
    if (typeof window !== 'undefined') {
        (window as any).replicantNukeDevDB = () => {
            void db.nukeDB().then(() => {
                logger.info('LocalStorage DB Nuked.');
            });
        };
    }

    return db;
}

export function getAllKeysFromLocalStorage(storage?: Storage): string[] {
    const localStorage = storage ?? browserLocalStorage;

    const keys: string[] = [];
    let i = 0;
    let key = localStorage.key(i++);
    const MAX_KEYS = 5000;

    while (key && i < MAX_KEYS) {
        keys.push(key);
        key = localStorage.key(i++);
    }

    if (i === MAX_KEYS) {
        throw new Error('Reached ' + MAX_KEYS + ' for local storage');
    }

    return keys;
}

// TODO: Consider combining with db.
export function getAllUserIDsFromLocalStorage(storage?: Storage, appPrefix?: string): string[] {
    const keys = getAllKeysFromLocalStorage(storage);
    const prefix = KEY_PREFIX + (appPrefix ? '_' + appPrefix : '') + '_state_';

    return keys.filter((x) => x.startsWith(prefix)).map((x) => x.substr(prefix.length));
}

export function getAllScheduledActionKeysFromLocalStorage(storage?: Storage, appPrefix?: string): ScheduledActionKey[] {
    const keys = getAllKeysFromLocalStorage(storage);
    const prefix = KEY_PREFIX + '_' + scheduledActionKeyPrefix(appPrefix || '', '');

    return keys
        .filter((x) => x.startsWith(prefix))
        .map((x) => {
            const parts = x.split('_');
            return { timeslotId: Number(parts[parts.length - 2]), actionId: parts[parts.length - 1]! };
        });
}

function validateUsernameForLocalstorage(username: string) {
    if (username.includes('_messages') || username.includes('_changesets')) {
        throw new Error('Username is invalid to be stored in LocalStorage!');
    }
}

export default createLocalStorageDB;
